// src/components/AdminActions.jsx
import React, { useState, useEffect } from 'react';
import { Trash2, MessageSquare, StickyNote } from 'lucide-react';

const AdminActions = ({ 
    featureRef, 
    organization,
    currentUser,
    className = "" 
}) => {
    if (!currentUser?.is_admin) return null;
    const [adminComment, setAdminComment] = useState('');
    const [privateNote, setPrivateNote] = useState('');
    const [adminComments, setAdminComments] = useState([]);
    const [privateNotes, setPrivateNotes] = useState([]);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('comments'); // 'comments' or 'notes'

    useEffect(() => {
        if (!user?.is_admin || !organization?.id) return;

        const fetchAdminContent = async () => {
            try {
                // Fetch both comments and notes
                const [commentsRes, notesRes] = await Promise.all([
                    fetch(`/api/admin-comments/${organization.id}/${featureRef}/comments`, {
                        credentials: 'include'
                    }),
                    fetch(`/api/admin-comments/${organization.id}/${featureRef}/notes`, {
                        credentials: 'include'
                    })
                ]);

                if (!commentsRes.ok || !notesRes.ok) {
                    throw new Error('Failed to fetch admin content');
                }

                const [comments, notes] = await Promise.all([
                    commentsRes.json(),
                    notesRes.json()
                ]);

                setAdminComments(comments);
                setPrivateNotes(notes);
            } catch (error) {
                console.error('Error fetching admin content:', error);
                setError('Failed to load admin content');
            }
        };

        fetchAdminContent();
    }, [featureRef, organization?.id, user?.is_admin]);

    const handleSubmitComment = async (e) => {
        e.preventDefault();
        if (!adminComment.trim()) return;

        try {
            const response = await fetch('/api/admin-comments/comments', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    feature_reference: featureRef,
                    organization_id: organization.id,
                    comment_text: adminComment
                })
            });

            if (!response.ok) {
                throw new Error('Failed to add comment');
            }

            const newComment = await response.json();
            setAdminComments(prev => [newComment, ...prev]);
            setAdminComment('');
        } catch (error) {
            console.error('Error adding admin comment:', error);
            setError('Failed to add comment');
        }
    };

    const handleSubmitNote = async (e) => {
        e.preventDefault();
        if (!privateNote.trim()) return;

        try {
            const response = await fetch('/api/admin-comments/notes', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    feature_reference: featureRef,
                    organization_id: organization.id,
                    note_text: privateNote
                })
            });

            if (!response.ok) {
                throw new Error('Failed to add note');
            }

            const newNote = await response.json();
            setPrivateNotes(prev => [newNote, ...prev]);
            setPrivateNote('');
        } catch (error) {
            console.error('Error adding private note:', error);
            setError('Failed to add note');
        }
    };

    const handleDelete = async (id, type) => {
        try {
            const response = await fetch(`/api/admin-comments/${type}/${id}`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error(`Failed to delete ${type}`);
            }

            if (type === 'comments') {
                setAdminComments(prev => prev.filter(comment => comment.id !== id));
            } else {
                setPrivateNotes(prev => prev.filter(note => note.id !== id));
            }
        } catch (error) {
            console.error(`Error deleting ${type}:`, error);
            setError(`Failed to delete ${type}`);
        }
    };

    if (!user?.is_admin) return null;

    return (
        <div className={`bg-amber-50 p-4 rounded-lg border border-amber-200 ${className}`}>
            <div className="flex gap-2 mb-4">
                <button
                    onClick={() => setActiveTab('comments')}
                    className={`flex items-center gap-1 px-3 py-1 rounded ${
                        activeTab === 'comments' 
                            ? 'bg-amber-200 text-amber-900' 
                            : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                    }`}
                >
                    <MessageSquare className="w-4 h-4" />
                    Admin Comments
                </button>
                <button
                    onClick={() => setActiveTab('notes')}
                    className={`flex items-center gap-1 px-3 py-1 rounded ${
                        activeTab === 'notes' 
                            ? 'bg-amber-200 text-amber-900' 
                            : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                    }`}
                >
                    <StickyNote className="w-4 h-4" />
                    Private Notes
                </button>
            </div>

            {error && (
                <div className="text-red-600 text-sm mb-2">{error}</div>
            )}

            {activeTab === 'comments' ? (
                <>
                    <form onSubmit={handleSubmitComment} className="mb-4">
                        <textarea
                            value={adminComment}
                            onChange={(e) => setAdminComment(e.target.value)}
                            placeholder="Add an admin comment (will appear in reports)..."
                            className="w-full px-3 py-2 border border-amber-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            rows={2}
                        />
                        <div className="flex justify-end mt-2">
                            <button
                                type="submit"
                                disabled={!adminComment.trim()}
                                className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Add Comment
                            </button>
                        </div>
                    </form>

                    <div className="space-y-3">
                        {adminComments.map(comment => (
                            <div 
                                key={comment.id} 
                                className="bg-white p-3 rounded-lg border border-amber-200"
                            >
                                <div className="flex justify-between items-start mb-2">
                                    <div>
                                        <span className="font-medium text-amber-900">
                                            {comment.user_name}
                                        </span>
                                        <span className="text-xs text-amber-700 ml-2">
                                            {new Date(comment.created_at).toLocaleString()}
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => handleDelete(comment.id, 'comments')}
                                        className="text-amber-700 hover:text-amber-900"
                                        title="Delete comment"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                                <p className="text-sm text-amber-800 whitespace-pre-wrap">
                                    {comment.comment_text}
                                </p>
                            </div>
                        ))}
                        {adminComments.length === 0 && (
                            <p className="text-sm text-amber-700 text-center py-2">
                                No admin comments yet
                            </p>
                        )}
                    </div>
                </>
            ) : (
                <>
                    <form onSubmit={handleSubmitNote} className="mb-4">
                        <textarea
                            value={privateNote}
                            onChange={(e) => setPrivateNote(e.target.value)}
                            placeholder="Add a private note (will not appear in reports)..."
                            className="w-full px-3 py-2 border border-amber-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            rows={2}
                        />
                        <div className="flex justify-end mt-2">
                            <button
                                type="submit"
                                disabled={!privateNote.trim()}
                                className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Add Note
                            </button>
                        </div>
                    </form>

                    <div className="space-y-3">
                        {privateNotes.map(note => (
                            <div 
                                key={note.id} 
                                className="bg-white p-3 rounded-lg border border-amber-200"
                            >
                                <div className="flex justify-between items-start mb-2">
                                    <div>
                                        <span className="font-medium text-amber-900">
                                            {note.user_name}
                                        </span>
                                        <span className="text-xs text-amber-700 ml-2">
                                            {new Date(note.created_at).toLocaleString()}
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => handleDelete(note.id, 'notes')}
                                        className="text-amber-700 hover:text-amber-900"
                                        title="Delete note"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                                <p className="text-sm text-amber-800 whitespace-pre-wrap">
                                    {note.note_text}
                                </p>
                            </div>
                        ))}
                        {privateNotes.length === 0 && (
                            <p className="text-sm text-amber-700 text-center py-2">
                                No private notes yet
                            </p>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default AdminActions;